import React, { useId, useRef, useState } from "react";
import { Button, Card, Col, Form, Input, Row, Space, Tooltip, Typography } from "antd";
import { CloseOutlined, DeleteOutlined, EyeOutlined, FilePdfOutlined, HolderOutlined } from "@ant-design/icons";
import { Localize, EntityRemoveButton } from "components/service";
import { useTranslation } from "components/use-hooks";
import notifyMutationError from "components/request-result/notify-mutation-error";
import { documentUseMutation } from "../../hooks";
import DocumentConst from "../../document-const";
import DocumentPdfField from "../fields/document-pdf-field";
import DocumentZoneSelect from "../fields/document-zone-select";
import DocumentActiveField from "../fields/document-active-field";

const safeDocumentUrl = (value) => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
};

const documentFileName = (url, fallback) => {
  if (!url) return fallback;
  const fileName = new URL(url).pathname.split("/").filter(Boolean).pop();
  if (!fileName) return fallback;
  try {
    return decodeURIComponent(fileName).trim() || fallback;
  } catch {
    return fileName;
  }
};

const DocumentEditForm = ({
  document = {}, onSaved, onDeleted, onCancel, autoFocus = false, visible = true,
  disabled = false, onBusyChange, prepareCreate = async () => ({}), dragHandle,
}) => {
  const [form] = Form.useForm();
  const formId = useId();
  const translations = useTranslation();
  const [savedDocument, setSavedDocument] = useState(document);
  const [pending, setPending] = useState(false);
  const busy = useRef(false);
  const watchedTitle = Form.useWatch("title", form);
  const watchedFile = Form.useWatch("file", form);
  const watchedPlacement = Form.useWatch("placement", form);
  const watchedActive = Form.useWatch("active", form);
  const { _setDocumentCreateUpdate, loadingMutationDocumentCreateUpdate } =
    documentUseMutation.createUpdate(savedDocument.id);
  const { _setMutationDocumentDelete, loadingMutationDocumentDelete } =
    documentUseMutation.delete(savedDocument.id);
  const loading = pending || loadingMutationDocumentCreateUpdate || loadingMutationDocumentDelete;
  const controlsDisabled = disabled || loading;
  const placementEnabled = DocumentConst.zoningEnabled && DocumentConst.zones.length > 0;
  const currentUrl = safeDocumentUrl(savedDocument.getFileUrl);
  const invalidPdfMessage = translations["APP_SETTINGS.Invalid_Pdf"];
  const baselineTitle = (savedDocument.title ?? "").trim();
  const baselinePlacement = savedDocument.placement ?? "";
  const baselineActive = savedDocument.id ? savedDocument.active ?? null : true;
  const currentTitle = watchedTitle === undefined ? savedDocument.title ?? "" : watchedTitle;
  const currentPlacement = watchedPlacement === undefined ? baselinePlacement : watchedPlacement ?? "";
  const placementDirty = placementEnabled && currentPlacement !== baselinePlacement;
  const currentActive = watchedActive === undefined ? baselineActive : watchedActive;
  const activeDirty = currentActive !== baselineActive;
  const isDirty = currentTitle.trim() !== baselineTitle || Boolean(watchedFile) || placementDirty || activeDirty;
  const statusKey = !savedDocument.id ? "APP_SETTINGS.New_Document"
    : isDirty ? "APP_SETTINGS.Status_Changed" : "APP_SETTINGS.Status_Saved";
  const sortingText = translations["APP_SETTINGS.Sorting_Unavailable"];
  const deleteText = translations["GLOBAL.Button_Text_Delete"];
  const cancelText = translations["APP_SETTINGS.Cancel_Draft"];
  const openText = translations["APP_SETTINGS.Open_Document"];
  const fileName = documentFileName(currentUrl, translations["APP_SETTINGS.Label_Pdf"]);

  const save = async ({ title, file, placement, active }) => {
    const nextTitle = title.trim();
    const nextPlacement = placement ?? "";
    const changedPlacement = placementEnabled && nextPlacement !== baselinePlacement;
    const nextActive = active ?? null;
    const changedActive = nextActive !== baselineActive;
    if (disabled || busy.current || (nextTitle === baselineTitle && !file && !changedPlacement && !changedActive)) return;
    if (placementEnabled && !nextPlacement.trim()) {
      form.setFields([{ name: "placement", errors: [translations["APP_SETTINGS.Required_Zone"]] }]);
      return;
    }
    busy.current = true;
    setPending(true);
    onBusyChange?.(true);
    const input = {};
    if (savedDocument.id) input.id = savedDocument.id;
    if (!savedDocument.id || nextTitle !== baselineTitle) input.title = nextTitle;
    if (placementEnabled && (!savedDocument.id || changedPlacement)) input.placement = nextPlacement;
    if (!savedDocument.id || changedActive) input.active = nextActive;
    if (file) input.file = file;

    try {
      if (!savedDocument.id) {
        try {
          const prepared = await prepareCreate();
          if (prepared?.row_order !== undefined) input.row_order = prepared.row_order;
        } catch (error) {
          notifyMutationError(error);
          return;
        }
      }
      const saved = await _setDocumentCreateUpdate({ variables: { input } });
      const savedTitle = saved.title ?? nextTitle;
      setSavedDocument({ ...saved, title: savedTitle });
      form.setFieldsValue({ title: savedTitle, placement: saved.placement ?? "", active: saved.active ?? null, file: undefined });
      await onSaved?.(saved);
    } catch {
      // The hook reports the error; keep this card's values and staged PDF for retry.
    } finally {
      busy.current = false;
      setPending(false);
      onBusyChange?.(false);
    }
  };

  const remove = async (options) => {
    if (disabled || busy.current) return false;
    busy.current = true;
    setPending(true);
    onBusyChange?.(true);
    try {
      const id = await _setMutationDocumentDelete(options);
      await onDeleted?.(id);
      return true;
    } catch {
      // Keep the confirmation open without reporting the hook's error twice.
      return false;
    } finally {
      busy.current = false;
      setPending(false);
      onBusyChange?.(false);
    }
  };

  return (
    <Form
      form={form}
      name={`document-${formId}`}
      layout="vertical"
      className="model-form edit-document-form"
      initialValues={{ title: document.title ?? "", placement: document.placement ?? "", active: document.id ? document.active ?? null : true }}
      disabled={controlsDisabled}
      onFinish={save}
    >
      <Card
        size="small"
        className="app-settings-document"
        title={<div className="app-settings-item-heading">
          {dragHandle ?? <Tooltip title={sortingText} open={visible ? undefined : false}>
            <span><Button className="app-settings-drag-handle" type="text" size="small" disabled icon={<HolderOutlined />} aria-label={sortingText} /></span>
          </Tooltip>}
          <Typography.Text type="secondary" className="app-settings-item-status"><Localize>{statusKey}</Localize></Typography.Text>
        </div>}
        extra={<Space size={4} wrap className="app-settings-item-actions">
          <DocumentActiveField form={form} />
          <Space size={8}>
          <Button type="primary" size="middle" htmlType="submit" loading={loadingMutationDocumentCreateUpdate} disabled={controlsDisabled || !isDirty}>
            <Localize>APP_SETTINGS.Save</Localize>
          </Button>
          {savedDocument.id ? <EntityRemoveButton
            confirmation="popover"
            visible={visible}
            tooltip={deleteText}
            modalButton={<Button type="text" danger size="middle" icon={<DeleteOutlined />} disabled={controlsDisabled} aria-label={deleteText} />}
            nameEntity={translations["APP_SETTINGS.Entity_Document"]}
            dataNameEntity={savedDocument.title ?? ""}
            loading={loadingMutationDocumentDelete}
            disabled={controlsDisabled}
            variables={{ id: savedDocument.id }}
            deleteMutation={remove}
          /> : <Tooltip title={cancelText} open={visible ? undefined : false}>
            <Button type="text" size="middle" icon={<CloseOutlined />} disabled={controlsDisabled} onClick={onCancel} aria-label={cancelText}>
              <Localize>GLOBAL.Button_Text_Cancel</Localize>
            </Button>
          </Tooltip>}
          </Space>
        </Space>}
      >
        <Row gutter={[24, 16]} className="app-settings-document-fields">
          <Col xs={24} md={12}>
            <Form.Item
              name="title"
              label={<Localize>APP_SETTINGS.Label_Title</Localize>}
              rules={[{ required: true, whitespace: true, message: <Localize>APP_SETTINGS.Required_Title</Localize> }]}
            >
              <Input name="title" size="small" autoFocus={autoFocus && visible} placeholder={translations["APP_SETTINGS.Placeholder_DocumentTitle"]} />
            </Form.Item>
            <DocumentZoneSelect
              form={form}
              initialPlacement={savedDocument.placement}
              disabled={controlsDisabled}
              preview={!placementEnabled}
            />
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="file"
              label={<Localize>APP_SETTINGS.Label_Pdf</Localize>}
              extra={<Localize>APP_SETTINGS.Pdf_Help</Localize>}
              rules={[{ required: !savedDocument.id, message: <Localize>APP_SETTINGS.Required_Pdf</Localize> }]}
            >
              <DocumentPdfField
                disabled={controlsDisabled}
                hasDocument={!!savedDocument.getFileUrl}
                onInvalid={() => form.setFields([{ name: "file", errors: [invalidPdfMessage] }])}
              />
            </Form.Item>
            {currentUrl && <div className="app-settings-document-file">
              <FilePdfOutlined aria-hidden="true" />
              <Tooltip title={fileName} open={visible ? undefined : false}>
                <Typography.Text className="app-settings-file-name" ellipsis>{fileName}</Typography.Text>
              </Tooltip>
              <Space size={4} className="app-settings-file-actions">
                <Tooltip title={openText} open={visible ? undefined : false}>
                  <Button type="text" size="small" icon={<EyeOutlined />} href={currentUrl} target="_blank" rel="noopener noreferrer" aria-label={openText} />
                </Tooltip>
                <Typography.Text copyable={{
                  text: currentUrl,
                  tooltips: [translations["APP_SETTINGS.Copy_Link"], translations["APP_SETTINGS.Copied_Link"]],
                }} />
              </Space>
            </div>}
          </Col>
        </Row>
      </Card>
    </Form>
  );
};

export default DocumentEditForm;
